import { useState, useCallback, useEffect, useMemo } from 'react';

const MAX_VISIBLE_DESKTOP = 3;
const MAX_VISIBLE_MOBILE = 2;
const MOBILE_BREAKPOINT = 640;

function useIsMobile() {
    const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT);
    useEffect(() => {
        const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
        const handler = (e) => setIsMobile(e.matches);
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, []);
    return isMobile;
}

function getImageSrc(item) {
    if (item.base64 && item.mimeType) {
        return `data:${item.mimeType};base64,${item.base64}`;
    }
    return item.thumbnailUrl || item.imageUrl || '';
}

function getFullSrc(item) {
    if (item.base64 && item.mimeType) {
        return `data:${item.mimeType};base64,${item.base64}`;
    }
    return item.imageUrl || item.thumbnailUrl || '';
}

function getGridLayout(count) {
    if (count === 1) return { className: 'grid-cols-1', height: 'h-[400px]' };
    if (count === 2) return { className: 'grid-cols-2', height: 'h-[340px]' };
    return { className: 'grid-cols-3', height: 'h-[240px]' };
}

function Lightbox({ items, index, onClose, onPrev, onNext }) {
    const item = items[index];

    useEffect(() => {
        const handleKey = (e) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'ArrowLeft') onPrev();
            if (e.key === 'ArrowRight') onNext();
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [onClose, onPrev, onNext]);

    return (
        <div
            className="fixed inset-0 z-[9999] bg-black/90 flex flex-col"
            onClick={onClose}
        >
            <div className="flex items-center justify-between p-4 shrink-0">
                <button
                    onClick={(e) => { e.stopPropagation(); onClose(); }}
                    className="w-8 h-8 flex items-center justify-center rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
                <span className="text-white/70 text-sm">{index + 1} / {items.length}</span>
                <div className="w-8" />
            </div>

            <div className="flex-1 flex items-center justify-center relative min-h-0 px-14">
                {items.length > 1 && (
                    <button
                        onClick={(e) => { e.stopPropagation(); onPrev(); }}
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                    >
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                )}

                <img
                    src={getFullSrc(item)}
                    alt={item.title || ''}
                    className="max-w-full max-h-full object-contain select-none"
                    onClick={(e) => e.stopPropagation()}
                />

                {items.length > 1 && (
                    <button
                        onClick={(e) => { e.stopPropagation(); onNext(); }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                    >
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                )}
            </div>

            <div className="p-4 shrink-0">
                {item.title && (
                    <p className="text-white text-sm font-medium text-center">{item.title}</p>
                )}
                {item.source && (
                    <p className="text-white/50 text-xs text-center mt-1">{item.source}</p>
                )}
            </div>
        </div>
    );
}

export default function Gallery({ items, title, aspect_ratio = 'auto' }) {
    const [lightboxIndex, setLightboxIndex] = useState(null);

    const openLightbox = useCallback((i) => setLightboxIndex(i), []);
    const closeLightbox = useCallback(() => setLightboxIndex(null), []);
    const goPrev = useCallback(() => {
        setLightboxIndex((prev) => (prev <= 0 ? items.length - 1 : prev - 1));
    }, [items?.length]);
    const goNext = useCallback(() => {
        setLightboxIndex((prev) => (prev >= items.length - 1 ? 0 : prev + 1));
    }, [items?.length]);

    const isMobile = useIsMobile();
    const maxVisible = isMobile ? MAX_VISIBLE_MOBILE : MAX_VISIBLE_DESKTOP;

    if (!items || items.length === 0) return null;

    const visibleItems = items.slice(0, maxVisible);
    const remainingCount = items.length - maxVisible;
    const layout = getGridLayout(visibleItems.length);

    const aspectClass = {
        square: 'aspect-square',
        landscape: 'aspect-video',
        portrait: 'aspect-[3/4]',
        auto: '',
    }[aspect_ratio] || '';

    const heightClass = aspect_ratio === 'auto' ? layout.height : '';

    return (
        <div>
            {title && (
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">{title}</div>
            )}
            <div className={`grid ${layout.className} gap-1.5`}>
                {visibleItems.map((item, i) => {
                    const isLast = i === visibleItems.length - 1;
                    const showBadge = isLast && remainingCount > 0;

                    return (
                        <div
                            key={i}
                            onClick={() => openLightbox(i)}
                            className={`relative overflow-hidden rounded-lg bg-gray-100 dark:bg-white/5 cursor-pointer group ${aspectClass} ${heightClass}`}
                        >
                            <img
                                src={getImageSrc(item)}
                                alt={item.title || ''}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                                loading="lazy"
                                onError={(e) => {
                                    e.target.src = getFullSrc(item);
                                }}
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                            {showBadge && (
                                <div className="absolute bottom-2 right-2 flex items-center gap-1 px-2 py-1 rounded-full bg-black/60 backdrop-blur-sm">
                                    <svg className="w-3.5 h-3.5 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                    <span className="text-white text-xs font-medium">{items.length}</span>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {lightboxIndex !== null && (
                <Lightbox
                    items={items}
                    index={lightboxIndex}
                    onClose={closeLightbox}
                    onPrev={goPrev}
                    onNext={goNext}
                />
            )}
        </div>
    );
}
